import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, useMap, useMapEvents, AttributionControl, ZoomControl } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { SearchAssetClusters } from "../../graphql/queries/searchAssetClusters";
import { SearchAssetBoundingBox } from "../../graphql/queries/searchAssetBoundingBox";
import ClusterMarkers from "./ClusterMarkers";
import VectorTileLayer from "./VectorTileLayer";
import { IOT_STATUS_COLOR_MAP } from "../../config/iotConnectionStatusConfig";
import {
  MAP_PAN_BOUNDS,
  MapBounds,
  MapHomeRegion,
  getFillZoom,
  getInitialFitBounds,
  getMapHomeRegion,
  normalizeQueryBounds,
} from "../../utils/mapBounds";
import { useUserTimeZone } from "../../hooks/useUserTimeZone";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default view (Germany) — only used when no asset bounding box is available
const DEFAULT_CENTER: [number, number] = [51.1657, 10.4515];
const DEFAULT_ZOOM = 5;
const MAX_ZOOM = 18;
/**
 * Fractional so fitBounds can match the world overview to the widget instead of falling
 * back to the minimum zoom. A fractional snap only hurt Leaflet's own scroll-wheel
 * handler (it crawled in tiny animated steps); MapWheelZoomHandler and the +/- buttons
 * zoom by whole zoomDelta steps.
 */
const ZOOM_SNAP = 0.25;
const SINGLE_LOCATION_ZOOM = 16;
const FIT_PADDING: [number, number] = [50, 50];
const DEBOUNCE_MS = 300;
/**
 * Wheel events closer together than this belong to one scroll gesture. A trackpad or
 * Magic Mouse keeps emitting momentum events every ~16 ms for several hundred ms after
 * the finger stops; a notched wheel sends a short burst per notch, and notches of a fast
 * roll are ~125 ms apart.
 */
const WHEEL_GESTURE_GAP_MS = 100;

export interface MapViewState {
  zoom: number;
  center: { lat: number; lng: number };
  bounds: MapBounds;
}

interface GeoBoundingBox {
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
}

/**
 * Keeps blank bands above/below the world out of view: the minimum zoom is the zoom at
 * which the Web-Mercator world is exactly as tall as the container. Leaflet's
 * `setMinZoom` zooms in by itself when the current zoom drops below the new minimum.
 */
const applyFillConstraints = (map: L.Map) => {
  map.invalidateSize();
  const fillZoom = getFillZoom(map.getSize().y);
  if (fillZoom != null && map.getMinZoom() !== fillZoom) {
    map.setMinZoom(fillZoom);
  }
};

const readMapViewState = (map: L.Map): MapViewState => {
  const bounds = map.getBounds();
  const center = map.getCenter();
  return {
    zoom: map.getZoom(),
    center: { lat: center.lat, lng: center.lng },
    bounds: {
      northEast: {
        lat: bounds.getNorthEast().lat,
        lng: bounds.getNorthEast().lng,
      },
      southWest: {
        lat: bounds.getSouthWest().lat,
        lng: bounds.getSouthWest().lng,
      },
    },
  };
};

/**
 * Re-applies the fill constraints whenever the container is resized (breakpoint change,
 * side navigation toggle). The ResizeObserver also fires once on observe().
 */
const MapViewportConstraints: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const observer = new ResizeObserver(() => applyFillConstraints(map));
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
};

/**
 * Replaces Leaflet's scroll-wheel zoom. Leaflet evaluates wheel events in 40 ms windows
 * and zooms a whole step per window, so one inertial scroll on a trackpad/Magic Mouse
 * zooms 1–3 steps depending on momentum. Here one gesture (a continuous stream of wheel
 * events) zooms exactly one step around the pointer, like the +/- buttons.
 */
const MapWheelZoomHandler: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let lastEventAt = 0;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      // event.timeStamp is the input time, so a busy main thread cannot split one gesture in two.
      const continuesGesture = event.timeStamp - lastEventAt < WHEEL_GESTURE_GAP_MS;
      lastEventAt = event.timeStamp;
      if (continuesGesture || !event.deltaY) return;

      const zoom = map.getZoom();
      const zoomingIn = event.deltaY < 0;
      if (zoomingIn ? zoom >= map.getMaxZoom() : zoom <= map.getMinZoom()) return;

      const delta = map.options.zoomDelta ?? 1;
      map.setZoomAround(map.mouseEventToContainerPoint(event), zoom + (zoomingIn ? delta : -delta));
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [map]);

  return null;
};

interface MapViewControllerProps {
  /** Bounding box of all visible assets (searchAssetBoundingBox). */
  initialBounds: GeoBoundingBox | undefined;
  /** True once the bounding-box query has finished — with data, empty or with an error. */
  initialBoundsSettled: boolean;
  /** Continent the overview is centred on when the assets span the world. */
  homeRegion: MapHomeRegion;
  /** Fitted instead of the asset bbox when set (full-screen map opened from the widget). */
  initialViewBounds?: MapBounds;
  onViewChange: (state: MapViewState) => void;
}

/**
 * Applies the initial view (fit to all assets) and reports every subsequent view
 * change, debounced. View changes before the initial fit are swallowed so the widget
 * sends exactly one cluster query for the first paint instead of one for the default
 * view and one for the fitted view.
 *
 * The initial view is re-applied whenever the container is resized until the user
 * touches the map: the dashboard grid renders at a default width and measures its real
 * one a few ms later. With the asset bbox already cached (navigating back to the
 * dashboard), the first fit would otherwise happen at the provisional size and the
 * grown container would show a zoomed-out, repeating world.
 */
const MapViewController: React.FC<MapViewControllerProps> = ({
  initialBounds,
  initialBoundsSettled,
  homeRegion,
  initialViewBounds,
  onViewChange,
}) => {
  const map = useMap();
  const initialViewAppliedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const scheduleUpdate = useCallback(() => {
    if (!initialViewAppliedRef.current) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onViewChange(readMapViewState(map));
    }, DEBOUNCE_MS);
  }, [map, onViewChange]);

  // Registered before the fit effect below so the listener exists when the
  // (synchronous, non-animated) initial fit fires its moveend.
  useMapEvents({
    moveend: scheduleUpdate,
    zoomend: scheduleUpdate,
  });

  const applyInitialView = useCallback(() => {
    // Min zoom must be in place before fitBounds consults it.
    applyFillConstraints(map);

    if (initialViewBounds) {
      // A world-wide view (the widget at its overview or minimum zoom) would repeat the
      // world in a wider container — it gets the continent overview like the asset bbox.
      const viewFit = getInitialFitBounds(initialViewBounds, homeRegion);
      map.fitBounds(
        L.latLngBounds(
          [viewFit.southWest.lat, viewFit.southWest.lng],
          [viewFit.northEast.lat, viewFit.northEast.lng],
        ),
        { padding: viewFit === initialViewBounds ? [0, 0] : FIT_PADDING, animate: false },
      );
      return;
    }

    const bounds = initialBounds;
    if (!bounds) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
      return;
    }

    if (bounds.southwest.lat === bounds.northeast.lat && bounds.southwest.lng === bounds.northeast.lng) {
      map.setView([bounds.southwest.lat, bounds.southwest.lng], SINGLE_LOCATION_ZOOM, { animate: false });
      return;
    }

    const fitBounds = getInitialFitBounds(
      {
        southWest: { lat: bounds.southwest.lat, lng: bounds.southwest.lng },
        northEast: { lat: bounds.northeast.lat, lng: bounds.northeast.lng },
      },
      homeRegion,
    );
    const leafletBounds = L.latLngBounds(
      [fitBounds.southWest.lat, fitBounds.southWest.lng],
      [fitBounds.northEast.lat, fitBounds.northEast.lng],
    );
    map.fitBounds(leafletBounds, { padding: FIT_PADDING, animate: false });
  }, [initialBounds, homeRegion, initialViewBounds, map]);

  useEffect(() => {
    if (initialViewAppliedRef.current || (!initialViewBounds && !initialBoundsSettled)) return;
    initialViewAppliedRef.current = true;
    applyInitialView();
  }, [initialBoundsSettled, initialViewBounds, applyInitialView]);

  useEffect(() => {
    const container = map.getContainer();
    const markInteracted = () => {
      userInteractedRef.current = true;
    };
    const observer = new ResizeObserver(() => {
      if (initialViewAppliedRef.current && !userInteractedRef.current) applyInitialView();
    });
    observer.observe(container);
    const interactionEvents = ["pointerdown", "wheel", "keydown"] as const;
    interactionEvents.forEach((type) => container.addEventListener(type, markInteracted, { capture: true }));
    return () => {
      observer.disconnect();
      interactionEvents.forEach((type) => container.removeEventListener(type, markInteracted, { capture: true }));
    };
  }, [map, applyInitialView]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return null;
};

export interface AssetLocationsMapStatus {
  /** Assets in the current view; undefined until the first cluster result. */
  assetCount?: number;
  loading: boolean;
}

interface AssetLocationsMapProps {
  /** Title bar drawn over the map (widget header or the full-screen logo block). */
  renderOverlay: (status: AssetLocationsMapStatus) => React.ReactNode;
  /** Fitted instead of the asset bbox when set. */
  initialViewBounds?: MapBounds;
  onViewChange?: (state: MapViewState) => void;
}

/**
 * The asset-locations map with cluster markers, shared by the dashboard widget and the
 * full-screen view (`?map=fullscreen`). Fills its parent.
 */
const AssetLocationsMap: React.FC<AssetLocationsMapProps> = ({
  renderOverlay,
  initialViewBounds,
  onViewChange,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const homeRegion = getMapHomeRegion(useUserTimeZone());
  const [mapState, setMapState] = useState<MapViewState | null>(null);

  const { data: boundingBoxData, loading: boundingBoxLoading } = useQuery(SearchAssetBoundingBox, {
    fetchPolicy: "cache-first",
  });

  const boundsVariables = useMemo(() => {
    if (!mapState) return null;
    const normalized = normalizeQueryBounds(mapState.bounds);
    return {
      bounds: {
        northeast: { lat: normalized.northEast.lat, lng: normalized.northEast.lng },
        southwest: { lat: normalized.southWest.lat, lng: normalized.southWest.lng },
      },
    };
  }, [mapState]);

  const { data, loading, previousData } = useQuery(SearchAssetClusters, {
    variables: boundsVariables!,
    skip: !boundsVariables,
    fetchPolicy: "network-only",
  });

  const displayData = data ?? previousData;
  const assetCount = displayData?.searchAssetClusters?.assetCount;

  const handleMapChange = useCallback((state: MapViewState) => {
    setMapState(state);
    onViewChange?.(state);
  }, [onViewChange]);

  const handleSingleLocationClick = useCallback((center: { lat: number; lng: number }) => {
    const geolocationFilter = JSON.stringify({ point: { lat: center.lat, lng: center.lng } });
    navigate(`/assets?geolocation=${encodeURIComponent(geolocationFilter)}`);
  }, [navigate]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <style>{`
        .donut-cluster-marker { background: none !important; border: none !important; }
        .map-widget-container .leaflet-control-zoom { margin-bottom: 10px; margin-left: 10px; }
      `}</style>
      {renderOverlay({
        assetCount: assetCount != null ? Math.round(assetCount) : undefined,
        loading: loading || boundingBoxLoading,
      })}
      {/* Map fills entire widget - no-drag so map pan/zoom works */}
      <div className="no-drag" style={{ width: "100%", height: "100%" }}>
      <MapContainer
        className="map-widget-container"
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        zoomSnap={ZOOM_SNAP}
        maxZoom={MAX_ZOOM}
        maxBounds={MAP_PAN_BOUNDS}
        maxBoundsViscosity={1}
        style={{
          width: "100%",
          height: "100%",
        }}
        scrollWheelZoom={false} // replaced by MapWheelZoomHandler (one step per scroll gesture)
        zoomControl={false}
        attributionControl={false}
      >
        <ZoomControl position="bottomleft" />
        <AttributionControl
          position="bottomright"
          prefix={'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'}
        />
        <VectorTileLayer language={language} />
        <MapViewportConstraints />
        <MapWheelZoomHandler />
        <MapViewController
          initialBounds={boundingBoxData?.searchAssetBoundingBox}
          initialBoundsSettled={!boundingBoxLoading}
          homeRegion={homeRegion}
          initialViewBounds={initialViewBounds}
          onViewChange={handleMapChange}
        />
        {displayData?.searchAssetClusters?.clusters && (
          <ClusterMarkers
            clusters={displayData.searchAssetClusters.clusters}
            colorMap={IOT_STATUS_COLOR_MAP}
            locale={language}
            onSingleLocationClick={handleSingleLocationClick}
          />
        )}
      </MapContainer>
      </div>
    </div>
  );
};

export default AssetLocationsMap;
